import * as Clipboard from 'expo-clipboard';
import * as Haptics from 'expo-haptics';
import { useCallback, useEffect, useState } from 'react';
import { Platform, Share } from 'react-native';

/**
 * A thing whose link can be shared — a referable deal or the app invite.
 * The caller owns URL resolution because deals mint a per-deal campaign
 * link while the invite mints the user's single generic one.
 */
export interface ShareProgram {
  /** Stable id, used to scope the in-flight spinner to one card. */
  id: number;
  /** Find-or-mint the share URL. Rejects if the link can't be created. */
  resolveUrl: () => Promise<string>;
  /**
   * Find-or-mint the URL to encode in a QR code, when it differs from the
   * share URL (deals mint a source='qr' link so scans attribute as QR).
   * Falls back to resolveUrl.
   */
  resolveQrUrl?: () => Promise<string>;
  /** Copy that precedes the URL in the system share sheet. */
  message: string;
  /** Caption shown under the QR code. */
  qrCaption: string;
}

/**
 * Share / copy / QR for referral links, shared by the Refer index and the
 * invite screen — both had their own copy of this, which is how the two
 * drifted into different toast wording and error handling.
 *
 * Owns the transient UI that always travels with sharing: the confirmation
 * toast, the QR sheet payload, and which program's share is in flight.
 */
export function useShareLink() {
  const [toast, setToast] = useState<string | null>(null);
  const [qr, setQr] = useState<{ url: string; caption: string } | null>(null);
  const [sharingId, setSharingId] = useState<number | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(timer);
  }, [toast]);

  /** Resolves the URL, surfacing a toast instead of throwing at the caller. */
  const resolve = useCallback(async (program: ShareProgram, forQr = false): Promise<string | null> => {
    try {
      return await (forQr && program.resolveQrUrl ? program.resolveQrUrl() : program.resolveUrl());
    } catch {
      setToast('Could not load your link — try again');
      return null;
    }
  }, []);

  const share = useCallback(
    async (program: ShareProgram) => {
      // One share at a time — a second tap mid-mint would race the first.
      if (sharingId !== null) return;
      setSharingId(program.id);
      try {
        const url = await resolve(program);
        if (!url) return;
        try {
          await Share.share({ message: `${program.message} ${url}`, url });
        } catch (error) {
          // Web Share is absent on most desktop browsers, so it rejects there
          // rather than opening a panel — fall back to copying. Skip that when
          // the user deliberately dismissed (AbortError) or double-invoked
          // (InvalidStateError); those are intentional no-ops.
          const name = error instanceof Error ? error.name : '';
          if (name !== 'AbortError' && name !== 'InvalidStateError') {
            await Clipboard.setStringAsync(url);
            setToast('Link copied');
          }
        }
      } finally {
        setSharingId(null);
      }
    },
    [resolve, sharingId],
  );

  const copy = useCallback(
    async (program: ShareProgram) => {
      const url = await resolve(program);
      if (!url) return;
      await Clipboard.setStringAsync(url);
      if (Platform.OS === 'ios') {
        void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
      }
      setToast('Link copied');
    },
    [resolve],
  );

  const showQr = useCallback(
    async (program: ShareProgram) => {
      const url = await resolve(program, true);
      if (!url) return;
      setQr({ url, caption: program.qrCaption });
    },
    [resolve],
  );

  const closeQr = useCallback(() => setQr(null), []);

  return { toast, qr, sharingId, share, copy, showQr, closeQr };
}
